import React, { useEffect, useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";

import { FrameThumb, Marked } from "../search/ResultGroups";
import { momentFrameTime } from "../../pages/videos/scenes/sceneThumbs";
import { fmt } from "../../pages/videos/scenes/sceneModel";
import { momentKey, momentLine, timeRange } from "./askModel";
import projectInfo from "./projectInfo";

const FEW = 4; // moments shown until "Show all"

/** {projectId: projectInfo} of the videos of moments, as they load */
export function useProjects(moments) {
  const [info, setInfo] = useState({});
  const ids = [...new Set(moments.map((m) => m.project_id))];
  const key = ids.join(",");
  useEffect(() => {
    let live = true;
    ids.forEach((id) =>
      projectInfo(id)
        .then((p) => live && setInfo((prev) => ({ ...prev, [id]: p })))
        .catch((error) => console.log(error)),
    );
    return () => {
      live = false;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return info;
}

function MomentCard({ m, info, highlight, onPlay }) {
  const line = momentLine(m);
  const play = (
    <span className="ak-thumb-play">
      <PlayArrowIcon />
    </span>
  );
  return (
    <button type="button" className="ak-moment" onClick={onPlay}>
      {info ? (
        <FrameThumb
          className="ak-thumb"
          folder={info.cdn}
          src={info.video}
          time={m.start_time}
          frameTime={momentFrameTime(m.start_time, m.end_time)}
          fallback={info.thumb}
        >
          {play}
        </FrameThumb>
      ) : (
        <span className="sr-thumb ak-thumb" aria-hidden="true">
          {play}
          <span className="sr-thumb-time">{fmt(m.start_time)}</span>
        </span>
      )}
      <span className="ak-moment-info">
        <span className="ak-moment-name">{m.project_name || (info && info.project_name)}</span>
        <span className="ak-moment-meta">{[line.speaker, timeRange(m.start_time, m.end_time)].filter(Boolean).join(" · ")}</span>
        {line.text ? (
          <span className="ak-moment-quote">
            “<Marked text={line.text} terms={highlight} />”
          </span>
        ) : (
          line.scene && (
            <span className="ak-moment-quote">
              <i>{line.scene}</i>
            </span>
          )
        )}
      </span>
    </button>
  );
}

// The moments an answer is about, as cards that play them
export default function Moments({ id, moments, highlight, onPlay }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const info = useProjects(moments);
  const n = moments.length;
  const shown = all ? moments : moments.slice(0, FEW);
  const matches = n === 1 ? t("1 match") : t("{{count}} matches", { count: n });
  return (
    <section className="ak-moments" aria-label={t("Moments")}>
      <div className="ak-moments-head">
        <h3 className="ak-overline">{t("Moments")}</h3>
        <span className="ak-moments-count">{n > FEW && !all ? t("{{shown}} of {{total}} matches", { shown: FEW, total: n }) : matches}</span>
      </div>
      <div id={id} className="ak-moment-grid">
        {shown.map((m, i) => (
          <MomentCard key={momentKey(m)} m={m} info={info[m.project_id]} highlight={highlight} onPlay={() => onPlay(i)} />
        ))}
      </div>
      {n > FEW && (
        <button type="button" className="ak-show" aria-expanded={all} aria-controls={id} onClick={() => setAll(!all)}>
          <span>{all ? t("Show fewer") : t("Show all {{count}} matches", { count: n })}</span>
          {all ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </button>
      )}
    </section>
  );
}
